import "../../../App.css";
import React, { useState, useEffect } from "react";
import CenterSpinner from "../../../components/CenterSpinner";
import { restApi } from "../../../util/api"
import { error_message } from "../../../util/messages";
import { Select, Checkbox } from "antd";
import PieChart from "../charts/PieChart";
import { group } from "console";

const CurrentOncologyDiagnosis: React.FC = () => {

  const [data, setData] = useState<any[]>([])
  const [filteredData, setFilteredData] = useState<any[]>([])
  const [ageRange, setAgeRange] = useState<any>("all")
  const [sex, setSex] = useState<any>("all")
  const [tumorType, setTumorType] = useState<any>("all")
  const [groupTumors, setGroupTumors] = useState(false)
  const [targetKey, setTargetKey] = useState("cancer")
  const [outcome, setOutcome] = useState<any>("all")

  const sexOptions = [ // duplicate fields from SWFormUtil SexOptions
    { label: "Todo", value: "all" },
    { label: "Niño", value: "male" },
    { label: "Niña", value: "female" },
    { label: "Indeterminado", value: "indeterminate" }
  ]

  const ageRangeOptions = [
    { label: "Todo", value: "all"},
    { label: "0-5", value: "[0,5]"},
    { label: "6-10", value: "[6,10]"},
    { label: "11-15", value: "[11,15]"},
    { label: "16-20", value: "[16,20]"},
    { label: "21+", value: "[21,200]"},
  ]

  const tumorCategoryOptions = [
    { label: "Todo", value: "all" },
    { label: "Hematológico", value: "hematological" },
    { label: "Sólido", value: "solid" },
    { label: "Nervioso central", value: "centralNervous" },
  ]

  const outcomes = [
    { label: "Todo", value: "all" },
    { label: "Recuperación", value: "hasEventRecovery" },
    { label: "Sepsis", value: "hasSepsis" },
    { label: "Choque septico", value: "hasSepticShock" },
    { label: "Sangrado", value: "hasBleeding" },
    { label: "Neutropenia persistente", value: "hasPersistentNeutropenia" },
    { label: "Enterocolitis", value: "hasNeutropenicEntercolitis" },
    { label: "Infección", value: "hasNosocomialInfection" },
    { label: "Hospitalización", value: "hasHospiceCare" },
    { label: "Transplante", value: "hasTransplant" },
    { label: "Muerte", value: "isDead" },
    { label: "Abandono", value: "abandoned" }
  ]

  const outcomeDateFields: {[key: string]: any} = {
    hasEventRecovery: "eventRecoveryDate",
    hasSepsis: "sepsisDate",
    hasSepticShock: "septicShockDate",
    hasBleeding: "bleedingDate",
    hasPersistentNeutropenia: "persistentNeutropenicDate",
    hasNeutropenicEntercolitis: "neutropenicEntercolitisDate",
    hasNosocomialInfection: "nosocomialInfectionDate",
    hasHospiceCare: "hospiceCareDate",
    hasTransplant: "transplantDate",
    isDead: null, // technically this is "deathDate" but we should present patients that have died before their final chemo date
    abandoned: null, // similarly, there is no date for abandoned patients (referring to those that have opted out of study)
  }

  const oncologyMeds = [
    "5-fluoracilo",
    "actinomycin d",
    "adriamycin",
    "araC",
    "asparaginase",
    "bleomycin",
    "carboplatine",
    "cisplatine",
    "cyclophosphamide",
    "cytarabine",
    "dacarbazine",
    "daunorubicin",
    "dexamentason",
    "doxorubicin",
    "etoposide",
    "isofamide",
    "mercaptopurine",
    "metotrexate",
    "prednisone",
    "vinblastin",
    "vincristine"
  ]

  const tumorCategories: { [key: string]: string[] } = {
    hematological: [
      "hodgkinsDisease",
      "acuteLymphocyticLeukemia",
      "myeloidLymphocyticLeukemia",
      "nonHodgkinLymphoma",
    ],
    solid: [
      "wilmsTumor",
      "hepaticTumors",
      "germCellTumors",
      "ewingsSarcoma",
      "primitiveNeuroectodermalTumor",
      "rabdomiosarcoma",
      "retinoblastoma",
      "osteosarcoma",
      "langerhansCellHystiocytosis"
    ],
    centralNervous: [
      "neuroblastoma",
      "medulumoblastoma",
      "suppratentorialPrimitiveNeuroectodermalTumors",
      "anaplasicEpendimoma",
      "lowGradeAstrocytomas",
      "highGradeAstrocytomas",
    ]
  }

  const ageRangeChangeHandler = (val: any) => {
    if (val == "all") setAgeRange(val)
    else setAgeRange(JSON.parse(val))
  }

  useEffect(() => {
    getData()
  },[])

  const getData = async () => {
    try {
      const { data } = await restApi().get<Record<"report", any[]>>(
        'report/current-oncology-diagnosis'
      )
      // console.log('cancer',data.report[0].cancerMedications[0].cancer)
      setData(data.report.filter(d => d.cancerMedications != null))
    } catch (e: any) {
      console.log('e',e)
      error_message(e.response?.data?.reason)
      Promise.reject(e)
    }
  }

  useEffect(() => {
    let filtered: any[] = []
    const today = new Date()
    
    data.forEach(d => {
      // set patient age
      const birthday = new Date(d.dob)
      let age = today.getFullYear() - birthday.getFullYear()
      if (
        today.getMonth() < birthday.getMonth() ||
        (today.getMonth() === birthday.getMonth() && today.getDate() < birthday.getDate())
      ) {
        age -= 1;
      }

      // assign data point for each individual cancer diagnosis
      d.cancerMedications.forEach((entry: any) => {
        // get last chemo date for this tumor
        let lastChemoDate = new Date
        let chemoDateAssigned = false
        if (entry.medication) {
          for (const med of entry.medication) {
            if (!med.doseEndDate || !oncologyMeds.includes(med.medName)) continue
            const newChemoDate = new Date(med.doseEndDate)
            if (!chemoDateAssigned) {
              lastChemoDate = newChemoDate
              chemoDateAssigned = true
            } else if (lastChemoDate < newChemoDate) {
              lastChemoDate = newChemoDate
            }
          }
        }

        const patientOutcomes = outcomes.map(out => out.value)
          .filter(outcome => {
            let validOutcomeDate = outcomeDateFields[outcome] ? new Date(d[outcomeDateFields[outcome]]) >= lastChemoDate : true
            return validOutcomeDate ? d[outcome] : false
          })

        let newData = {
          age: age,
          sex: d.sex,
          cancer: entry.cancer,
          tumorType:  Object.keys(tumorCategories).find(key => tumorCategories[key].includes(entry.cancer)),
          outcomes: patientOutcomes
        }
        filtered.push(newData)
      })
    })

    //filter by age
    if (ageRange !== "all") {
      filtered = filtered.filter(d => d.age >= ageRange[0] && d.age <= ageRange[1])
    }

    //filter by sex
    if (sex !== "all") {
      filtered = filtered.filter(d => sex == d.sex)
    }

    // filter by tumor type
    if (tumorType != "all") {
      filtered = filtered.filter(d => tumorType == d.tumorType)
    }

    // filter by outcome
    if (outcome != "all") {
      filtered = filtered.filter(d => d.outcomes.includes(outcome))
    }

    setFilteredData(filtered)
  }, [data, ageRange, sex, tumorType, outcome])

  useEffect(() => {
    if (groupTumors)
      setTargetKey('tumorType')
    else
      setTargetKey('cancer')
  }, [groupTumors])

  return (
    <div
      style={{
        margin: "2rem auto",
        width: "calc(90% + 24px)",
        backgroundColor: "white",
        borderRadius: "0.25rem",
        padding: "1rem 1rem",
        minHeight: "22rem",
      }} >
      <h2>Distribución porcentual por tipo de tumor de acuerdo a la edad y sexo de pacientes oncológicos.</h2>
      {data.length ? <>
        <div style={{
          display: "flex",
          flexWrap: "wrap",
          columnGap: "2rem",
          rowGap: "1rem",
          alignItems: "center",
        }}>
          <div>
            <label>Sexo:</label>
            <Select
              style={{width: 120}}
              options={sexOptions}
              onChange={setSex}
            />
          </div>
          <div>
            <label>Edad:</label>
            <Select
              style={{width: 120}}
              options={ageRangeOptions}
              onChange={ageRangeChangeHandler}
            />
          </div>
          <div>
            <label>Desenlaces:</label>
            <Select
              style={{width: 180}}
              options={outcomes}
              onChange={setOutcome}
            />
          </div>
          <div>
            <Checkbox
              onChange={(value) => {
                setGroupTumors(value.target.checked)
                setTumorType("all")
              }}
            >
              Distribución por conglomerados
            </Checkbox>
          </div>
          {!groupTumors && <div>
            <label>Distribución por tipo tumoral:</label>
            <Select
              style={{width: 150}}
              options={tumorCategoryOptions}
              onChange={setTumorType}
            />
          </div>}
        </div>
        <PieChart data={filteredData} targetKey={targetKey}/>
      </> : <CenterSpinner />}
    </div>
  )
};

export default CurrentOncologyDiagnosis;
